import React from 'react';
import { Box, Typography, Chip, alpha } from '@mui/material';
import { COLORS, FONT_SIZES, FONT_WEIGHTS } from '../../theme/constants';
import AppButton from '../AppButton';

/**
 * SectionHeader - Modern section title with optional icon and action
 * Features: Gradient icon badge, flexible layout
 */
const SectionHeader = ({
  title,
  subtitle,
  icon: Icon,
  action,
  onLearnMore,
  status,
  showDivider = false,
  color = COLORS.primary.main,
  size = 'medium', // 'small' | 'medium' | 'large'
  sx = {},
}) => {
  const sizes = {
    small: { title: FONT_SIZES.body1, icon: 28, iconInner: 16 },
    medium: { title: FONT_SIZES.h5, icon: 36, iconInner: 20 },
    large: { title: FONT_SIZES.h4, icon: 44, iconInner: 24 },
  };

  const currentSize = sizes[size] || sizes.medium;

  const trailingAction =
    action ??
    (onLearnMore ? (
      <AppButton variantType="text" size="small" onClick={onLearnMore}>
        Learn More
      </AppButton>
    ) : status ? (
      <Chip
        label={status}
        size="small"
        sx={{
          height: 22,
          backgroundColor:
            status === 'Configured' || status === 'Processed'
              ? COLORS.success.light
              : COLORS.warning.light,
          color:
            status === 'Configured' || status === 'Processed'
              ? COLORS.success.main
              : COLORS.warning.main,
          fontWeight: FONT_WEIGHTS.medium,
          fontSize: '0.7rem',
        }}
      />
    ) : null);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: subtitle ? 0.5 : 2,
        ...(showDivider ? {
          pb: 1.5,
          borderBottom: `1px solid ${COLORS.border.light}`,
        } : {}),
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {Icon && (
          <Box
            sx={{
              width: currentSize.icon,
              height: currentSize.icon,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.7)})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(color, 0.3)}`,
            }}
          >
            <Icon sx={{ fontSize: currentSize.iconInner, color: '#fff' }} />
          </Box>
        )}
        <Box>
          <Typography
            sx={{
              fontWeight: FONT_WEIGHTS.semiBold,
              fontSize: currentSize.title,
              color: COLORS.text.primary,
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: FONT_SIZES.body2,
                color: COLORS.text.secondary,
                mt: 0.25,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {trailingAction}
    </Box>
  );
};

export default SectionHeader;
